import { useEffect, useRef, useState } from "react";
import { api, ApiError, type TestauspalauteRivi } from "./api";
import { kieli, t, virheTeksti } from "./kieli";
import type { KaannosAvain } from "./kaannokset";
import { naytaPvmKello } from "./pvm";

/* ═══ TESTAUSPALAUTE (T487, Toni 11.10.2026 «T487 GO, viesti demon toimittajatunnukselle») ═══
   Vain demossa (TESTAUS=1). Kolme osaa: nimikirjaimet (selaimessa), Palaute-nappi jokaisella sivulla ja pääkäyttäjän
   koonti. Demon roolitunnukset ovat usean testaajan yhteiskäytössä (pääChat 11.10.): nimikirjaimet erottavat testaajat,
   ja ne muistetaan tässä selaimessa. Nimiä ei tallenneta — tunnuskoodi ja nimikirjaimet. */

const AVAIN = "sampo.testaaja.nimikirjaimet";
const KUUNTELIJAT = new Set<(v: string) => void>();

/** Nimikirjaimet tästä selaimesta. Selaimen tallennus voi puuttua (yksityinen ikkuna): silloin tyhjä, ei virhettä. */
export function lueNimikirjaimet(): string {
  try { return (window.localStorage.getItem(AVAIN) ?? "").slice(0, 8); } catch { return ""; }
}
export function asetaNimikirjaimet(v: string) {
  const puhdas = v.replace(/[^\p{L}.-]/gu, "").slice(0, 8);
  try { window.localStorage.setItem(AVAIN, puhdas); } catch { /* ei tallennusta: arvo elää vain tämän sivun ajan */ }
  for (const f of KUUNTELIJAT) f(puhdas);
}
export function useNimikirjaimet(): [string, (v: string) => void] {
  const [arvo, setArvo] = useState(lueNimikirjaimet);
  useEffect(() => { KUUNTELIJAT.add(setArvo); return () => { KUUNTELIJAT.delete(setArvo); }; }, []);
  return [arvo, asetaNimikirjaimet];
}

/** Laite: puhelin kun näkymä on kapea (sama raja kuin sivupalkin piilotus) tai kosketus kapealla näytöllä. */
export function laite(): "puhelin" | "tietokone" {
  const leveys = document.documentElement.clientWidth;
  const kosketus = window.matchMedia?.("(pointer: coarse)").matches ?? false;
  return leveys < 768 || (kosketus && leveys < 1024) ? "puhelin" : "tietokone";
}

export function NimikirjainKentta() {
  const [nk, setNk] = useNimikirjaimet();
  return (
    <label className="testaus__nimikirjaimet">{t("tpa.nimikirjaimet")}
      <input value={nk} maxLength={8} onChange={(e) => setNk(e.target.value)} placeholder={t("tpa.nimikirjaimet_esim")}
             data-testaus-nimikirjaimet="" autoComplete="off" />
    </label>
  );
}

const TYYPIT = ["puuttuu", "vaikea", "virhe", "hienosti"] as const;

/** Kiinteä Palaute-nappi + ikkuna. Teksti säilyy saman sivun uudelleenavauksessa (pääChat 11.10. kohta 3). */
export function PalauteNappi({ osoite }: { osoite: string }) {
  const [auki, setAuki] = useState(false);
  const [luonnokset, setLuonnokset] = useState<Record<string, { tyyppi: string; teksti: string }>>({});
  const [tila, setTila] = useState<"" | "lahetetaan" | "lahetetty">("");
  const [virhe, setVirhe] = useState<string | null>(null);
  const [nk, setNk] = useNimikirjaimet();
  const ikkuna = useRef<HTMLDivElement>(null);
  const nykyOsoite = (window.location.hash.startsWith("#/") ? window.location.hash : osoite).slice(0, 300);
  const luonnos = luonnokset[nykyOsoite] ?? { tyyppi: "", teksti: "" };
  const aseta = (muutos: Partial<{ tyyppi: string; teksti: string }>) =>
    setLuonnokset((l) => ({ ...l, [nykyOsoite]: { ...luonnos, ...muutos } }));

  useEffect(() => {
    if (!auki) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setAuki(false); };
    // Napautus ikkunan ulkopuolelle sulkee — Palaute-nappi itse ei (se vaihtaa tilaa omalla klikkauksellaan).
    const ulko = (e: PointerEvent) => {
      const kohde = e.target as Node;
      if (ikkuna.current && !ikkuna.current.contains(kohde) && !(kohde as Element).closest?.("[data-palaute-nappi]")) setAuki(false);
    };
    document.addEventListener("keydown", esc);
    document.addEventListener("pointerdown", ulko);
    return () => { document.removeEventListener("keydown", esc); document.removeEventListener("pointerdown", ulko); };
  }, [auki]);

  async function laheta() {
    setTila("lahetetaan"); setVirhe(null);
    try {
      await api.lahetaSivupalaute({ tyyppi: luonnos.tyyppi, teksti: luonnos.teksti.trim(), osoite: nykyOsoite,
                                    laite: laite(), kieli: kieli(), nimikirjaimet: nk });
      setLuonnokset((l) => { const u = { ...l }; delete u[nykyOsoite]; return u; });
      setTila("lahetetty");
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("tpa.lahetys_virhe"));
      setTila("");
    }
  }
  const syy = !luonnos.tyyppi && !luonnos.teksti.trim() ? t("tpa.syy_tyyppi_teksti")
    : !luonnos.tyyppi ? t("tpa.syy_tyyppi") : !luonnos.teksti.trim() ? t("tpa.syy_teksti") : null;

  return (
    <>
      <div className="palaute-tila" aria-hidden="true" />
      <button type="button" className="palaute-nappi" data-palaute-nappi="" aria-expanded={auki}
              onClick={() => { setAuki(!auki); setTila(""); setVirhe(null); }}>{t("tpa.nappi")}</button>
      {auki && (
        <div className="palaute-ikkuna" role="dialog" aria-modal="false" aria-labelledby="palaute-otsikko" ref={ikkuna}
             data-palaute-ikkuna="">
          <div className="palaute-ikkuna__yla">
            <h3 id="palaute-otsikko">{t("tpa.otsikko")}</h3>
            <button type="button" className="palaute-ikkuna__sulje" aria-label={t("tpa.sulje")} onClick={() => setAuki(false)}>×</button>
          </div>
          <div className="palaute-ikkuna__meta mono" data-palaute-meta="">
            {nykyOsoite} · {kieli()} · {t(laite() === "puhelin" ? "tpa.puhelin" : "tpa.tietokone")}
          </div>
          {tila === "lahetetty" ? (
            <p className="huom" role="status" data-palaute-lahetetty="">{t("tpa.kiitos")}</p>
          ) : (<>
            <div className="palaute-ikkuna__tyypit" role="group" aria-label={t("tpa.tyyppi")}>
              {TYYPIT.map((ty) => (
                <button key={ty} type="button" className="valintanappi" aria-pressed={luonnos.tyyppi === ty}
                        data-palaute-tyyppi={ty} onClick={() => aseta({ tyyppi: ty })}>{t(`tpa.tyyppi.${ty}` as KaannosAvain)}</button>
              ))}
            </div>
            <label>{t("tpa.mita_huomasit")}
              <textarea rows={4} value={luonnos.teksti} maxLength={4000} onChange={(e) => aseta({ teksti: e.target.value })}
                        data-palaute-teksti="" />
            </label>
            <label>{t("tpa.nimikirjaimet")}
              <input value={nk} maxLength={8} onChange={(e) => setNk(e.target.value)} placeholder={t("tpa.nimikirjaimet_esim")} autoComplete="off" />
            </label>
            {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
            <div className="palaute-ikkuna__toiminnot">
              <button type="button" className="nappi nappi--paa" disabled={!!syy || tila === "lahetetaan"}
                      data-palaute-laheta="" onClick={() => void laheta()}>{t("tpa.laheta")}</button>
              <button type="button" className="nappi nappi--kevyt" onClick={() => setAuki(false)}>{t("tpa.peruuta")}</button>
              {syy && <span className="este-syy" data-este-syy="">{syy}</span>}
            </div>
          </>)}
          <p className="ikkuna__vihje">{t("tpa.kuka_nakee")}</p>
        </div>
      )}
    </>
  );
}

/* ── Pääkäyttäjän koonti: lista ja suodattimet ───────────────────────────────── */

const ROOLIT = ["projektipaallikko", "tyonjohtaja", "hitsaaja", "tilaaja"] as const;

/** T487b: «Uutta testattavaa» -ilmoitukset. Tarkistusviikolla luonnos lähtee klo 7.00 ellei sitä peruta tästä. */
function Ilmoitukset() {
  const [lista, setLista] = useState<Awaited<ReturnType<typeof api.testausIlmoitukset>> | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const hae = () => api.testausIlmoitukset().then(setLista).catch(() => setLista([]));
  useEffect(() => { void hae(); }, []);
  if (!lista || lista.length === 0) return null;
  return (
    <section className="panel" data-testaus-ilmoitukset={lista.length}>
      <div className="panel__head"><h2>{t("tpa.ilmoitukset_otsikko")}</h2></div>
      <div style={{ padding: "8px 12px" }}>
        {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
        {lista.map((i) => (
          <div key={i.id} className="testaus__ilmoitus" data-ilmoitus={i.id} data-ilmoitus-tila={i.tila}>
            <strong>{t(`tpa.ilmoitus_tila.${i.tila}` as KaannosAvain)}</strong>
            <span className="dim"> · {naytaPvmKello(i.lahetetty ?? i.peruttu ?? i.luotu)} · {t("tpa.riveja", { n: i.riveja })} · {i.sha_alku}..{i.sha_loppu}</span>
            {i.tila === "luonnos" && (
              <button type="button" className="nappi nappi--kevyt" style={{ marginLeft: 12 }} data-ilmoitus-peru={i.id}
                      onClick={() => { if (window.confirm(t("tpa.peru_vahvistus"))) void api.peruTestausIlmoitus(i.id).then(hae)
                        .catch((e) => setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("tpa.peru_virhe"))); }}>
                {t("tpa.peru")}</button>
            )}
            <ul className="dim">{i.rivit.map((r) => <li key={r.avain}>{kieli() !== "fi" ? r.en : r.fi}</li>)}</ul>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Testauspalaute() {
  const [rivit, setRivit] = useState<TestauspalauteRivi[] | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [rooli, setRooli] = useState("");
  const [laji, setLaji] = useState("");
  const [tyyppi, setTyyppi] = useState("");
  useEffect(() => {
    setRivit(null);
    api.testauspalautteet({ rooli, laji, tyyppi }).then((r) => { setRivit(r); setVirhe(null); })
      .catch((e) => setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("tpa.haku_virhe")));
  }, [rooli, laji, tyyppi]);
  const testaaja = (r: TestauspalauteRivi) => r.tunnuskoodi + (r.nimikirjaimet ? " · " + r.nimikirjaimet : "");
  return (
    <>
      <header className="head">
        <div className="eyebrow">{t("testaus.yla")}</div>
        <div className="head__rivi"><h1>{t("tpa.koonti_otsikko")}</h1></div>
      </header>
      <section className="panel">
        <div className="lomake__kentat" style={{ padding: 0 }}>
          <label>{t("tpa.suodata_rooli")}
            <select value={rooli} onChange={(e) => setRooli(e.target.value)} data-tpa-rooli="">
              <option value="">{t("tpa.kaikki")}</option>
              {ROOLIT.map((r) => <option key={r} value={r}>{t(`ohjeet.rooli.${r}` as KaannosAvain)}</option>)}
            </select>
          </label>
          <label>{t("tpa.suodata_laji")}
            <select value={laji} onChange={(e) => setLaji(e.target.value)} data-tpa-laji="">
              <option value="">{t("tpa.kaikki")}</option>
              <option value="tehtava">{t("tpa.laji.tehtava")}</option>
              <option value="loppu">{t("tpa.laji.loppu")}</option>
              <option value="sivu">{t("tpa.laji.sivu")}</option>
            </select>
          </label>
          <label>{t("tpa.suodata_tyyppi")}
            <select value={tyyppi} onChange={(e) => setTyyppi(e.target.value)} data-tpa-tyyppi="">
              <option value="">{t("tpa.kaikki")}</option>
              {TYYPIT.map((ty) => <option key={ty} value={ty}>{t(`tpa.tyyppi.${ty}` as KaannosAvain)}</option>)}
            </select>
          </label>
        </div>
      </section>
      <Ilmoitukset />
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      {!rivit && !virhe && <p className="dim">{t("yleinen.ladataan")}</p>}
      {rivit && rivit.length === 0 && <p className="dim" data-rivit="0" data-joukko="testauspalaute">{t("tpa.ei_palautetta")}</p>}
      {rivit && rivit.length > 0 && (
        <section className="panel">
          <table className="taulu" data-rivit={rivit.length} data-joukko="testauspalaute">
            <thead><tr>
              <th>{t("tpa.s_rooli")}</th><th>{t("tpa.s_kohta")}</th><th>{t("tpa.s_tulos")}</th><th>{t("tpa.s_teksti")}</th>
              <th>{t("tpa.s_testaaja")}</th><th>{t("tpa.s_aika")}</th>
            </tr></thead>
            <tbody>
              {rivit.map((r) => (
                <tr key={r.id} data-tpa-rivi={r.id}>
                  <td>{r.rooli ? t(`ohjeet.rooli.${r.rooli}` as KaannosAvain) : "—"}</td>
                  <td>{r.laji === "tehtava" ? `${r.tehtava_nro}. ${r.tehtava_teksti ?? ""}`
                       : r.laji === "loppu" ? t(`tpa.kysymys.${r.kysymys}` as KaannosAvain)
                       : <a href={r.osoite ?? "#/"}>{r.osoite}</a>}</td>
                  <td>{r.laji === "tehtava" ? (r.tulos ? t(`tpa.tulos.${r.tulos}` as KaannosAvain) : "—")
                       : r.laji === "loppu" ? (r.arvo ?? "—")
                       : t(`tpa.tyyppi.${r.tyyppi}` as KaannosAvain) + (r.laite ? ` · ${t(r.laite === "puhelin" ? "tpa.puhelin" : "tpa.tietokone")}` : "")}</td>
                  <td style={{ whiteSpace: "pre-wrap" }}>{r.teksti ?? "—"}</td>
                  <td className="mono">{testaaja(r)} · {r.kieli}</td>
                  <td>{naytaPvmKello(r.muokattu)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}
